'use client';

import React, { useMemo } from 'react';

export interface WhatsAppFormattedTextProps {
  text?: string | null;
  className?: string;
  style?: React.CSSProperties;
}

type InlineTokenType = 'text' | 'bold' | 'italic' | 'strike' | 'code' | 'link';

interface InlineToken {
  type: InlineTokenType;
  content?: string;
  children?: InlineToken[];
}

const INLINE_PATTERNS = [
  { type: 'code' as const, regex: /`([^`\n]+)`/ },
  { type: 'link' as const, regex: /https?:\/\/[^\s<]+[^<.,:;"\x27)\]\s]/ },
  { type: 'bold' as const, regex: /(^|[^\w*])\*([^\s*\n][^*\n]*?)\*(?=$|[^\w*])/u },
  { type: 'italic' as const, regex: /(^|[^\w_])_([^\s_\n][^_\n]*?)_(?=$|[^\w_])/u },
  { type: 'strike' as const, regex: /(^|[^\w~])~([^\s~\n][^~\n]*?)~(?=$|[^\w~])/u },
];

function tokenizeInline(str: string): InlineToken[] {
  if (!str) return [];
  let earliestIndex = -1;
  let bestMatch: {
    type: InlineTokenType;
    start: number;
    end: number;
    content: string;
  } | null = null;

  for (const p of INLINE_PATTERNS) {
    const m = str.match(p.regex);
    if (m && m.index !== undefined) {
      let matchStart = m.index;
      const matchEnd = m.index + m[0].length;
      let content = m[1] !== undefined ? m[1] : m[0];

      if (p.type === 'bold' || p.type === 'italic' || p.type === 'strike') {
        const prefix = m[1] || '';
        content = m[2] || '';
        matchStart = m.index + prefix.length;
        // WhatsApp rule: closing delimiter cannot be immediately preceded by whitespace
        if (/\s$/.test(content)) {
          continue;
        }
      }

      if (earliestIndex === -1 || matchStart < earliestIndex) {
        earliestIndex = matchStart;
        bestMatch = {
          type: p.type,
          start: matchStart,
          end: matchEnd,
          content,
        };
      }
    }
  }

  if (earliestIndex === -1 || !bestMatch) {
    return [{ type: 'text', content: str }];
  }

  const tokens: InlineToken[] = [];
  if (bestMatch.start > 0) {
    tokens.push({ type: 'text', content: str.slice(0, bestMatch.start) });
  }

  if (bestMatch.type === 'code' || bestMatch.type === 'link') {
    tokens.push({ type: bestMatch.type, content: bestMatch.content });
  } else {
    tokens.push({
      type: bestMatch.type,
      children: tokenizeInline(bestMatch.content),
    });
  }

  if (bestMatch.end < str.length) {
    tokens.push(...tokenizeInline(str.slice(bestMatch.end)));
  }

  return tokens;
}

function renderInlineTokens(tokens: InlineToken[], keyPrefix = 'tok'): React.ReactNode[] {
  return tokens.map((tok, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (tok.type) {
      case 'bold':
        return (
          <strong key={key} className="font-bold text-inherit">
            {tok.children ? renderInlineTokens(tok.children, `${key}-b`) : null}
          </strong>
        );
      case 'italic':
        return (
          <em key={key} className="italic text-inherit">
            {tok.children ? renderInlineTokens(tok.children, `${key}-i`) : null}
          </em>
        );
      case 'strike':
        return (
          <del key={key} className="line-through opacity-80 text-inherit">
            {tok.children ? renderInlineTokens(tok.children, `${key}-s`) : null}
          </del>
        );
      case 'code':
        return (
          <code
            key={key}
            className="font-mono text-[12.5px] bg-black/10 dark:bg-white/15 px-1 py-0.5 rounded font-normal select-text text-inherit"
          >
            {tok.content}
          </code>
        );
      case 'link':
        return (
          <a
            key={key}
            href={tok.content}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#027eb5] dark:text-[#53bdeb] hover:underline underline-offset-2 break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {tok.content}
          </a>
        );
      case 'text':
      default:
        return tok.content;
    }
  });
}

export function parseWhatsAppBlocks(text: string): React.ReactNode {
  if (!text) return null;

  // Split multi-line code blocks ``` ... ``` first
  const segments = text.split(/(```[\s\S]*?```)/g);

  return segments.map((seg, segIdx) => {
    if (!seg) return null;

    if (seg.startsWith('```') && seg.endsWith('```') && seg.length >= 6) {
      const codeContent = seg.slice(3, -3).replace(/^\n/, '').replace(/\n$/, '');
      return (
        <pre
          key={`codeblock-${segIdx}`}
          className="font-mono text-[12px] bg-black/10 dark:bg-black/30 p-2 rounded-md my-1 overflow-x-auto max-w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] font-normal text-inherit border border-black/5 dark:border-white/10"
        >
          <code className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{codeContent}</code>
        </pre>
      );
    }

    // Split into lines to support blockquotes (> quote)
    const lines = seg.split('\n');
    return (
      <React.Fragment key={`seg-${segIdx}`}>
        {lines.map((line, lineIdx) => {
          const isLast = lineIdx === lines.length - 1;
          const isQuote = line.startsWith('> ') || line.startsWith('>>> ');

          if (isQuote) {
            const prefixLen = line.startsWith('>>> ') ? 4 : 2;
            const quoteContent = line.slice(prefixLen);
            const tokens = tokenizeInline(quoteContent);
            return (
              <React.Fragment key={`line-${lineIdx}`}>
                <span className="block border-l-[3px] border-[#00a884] pl-2.5 my-1 text-inherit opacity-90 italic">
                  {renderInlineTokens(tokens, `q-${segIdx}-${lineIdx}`)}
                </span>
                {!isLast && '\n'}
              </React.Fragment>
            );
          }

          const tokens = tokenizeInline(line);
          return (
            <React.Fragment key={`line-${lineIdx}`}>
              {renderInlineTokens(tokens, `l-${segIdx}-${lineIdx}`)}
              {!isLast && '\n'}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });
}

export function WhatsAppFormattedText({ text, className = '', style }: WhatsAppFormattedTextProps) {
  const content = useMemo(() => {
    if (!text) return null;
    return parseWhatsAppBlocks(text);
  }, [text]);

  if (!content) return null;

  return (
    <span className={`select-text ${className}`.trim()} style={style}>
      {content}
    </span>
  );
}

export default WhatsAppFormattedText;
